import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  TuiDialogService,
  TuiError,
  TuiIcon,
  TuiButton,
  TuiInput,
  TuiTextfield,
  TuiNumberFormat,
} from '@taiga-ui/core';
import { TUI_CONFIRM, TuiInputNumber } from '@taiga-ui/kit';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, filter, Subject, switchMap, tap } from 'rxjs';
import { Product } from '@core/types/Product';
import { RequestProductSelectorComponent } from './components/request-product-selector/request-product-selector.component';
import { RequestService } from '@core/services/request/request.service';

enum FormFields {
  NewProduct = 'newProduct',
  NewProductAmount = 'newProductAmount',
}

@Component({
  selector: 'x-request-form-goods-request-product-adding',
  imports: [
    TuiTextfield,
    TuiNumberFormat,
    ReactiveFormsModule,
    TuiInput,
    TuiError,
    TuiButton,
    TuiInputNumber,
    TuiIcon,
    RequestProductSelectorComponent,
  ],
  templateUrl: './request-form-goods-request-product-adding.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormGoodsRequestProductAddingComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly $request = this.requestService.$request;
  private readonly destroyRef = inject(DestroyRef);
  FormFields = FormFields;
  formGroup = this.formBuilder.group({
    [FormFields.NewProduct]: this.formBuilder.control<Product | null>(null, [
      Validators.required,
    ]),
    [FormFields.NewProductAmount]: [
      1,
      [Validators.required, Validators.min(1)],
    ],
  });
  isAddNewRequestProductFormShown = false;

  addNewProductToRequest$ = new Subject<void>();

  @pure
  get newProductField() {
    return this.formGroup.controls[FormFields.NewProduct];
  }

  @pure
  get newProductAmountField() {
    return this.formGroup.controls[FormFields.NewProductAmount];
  }

  ngOnInit() {
    this.addNewProductToRequest$
      .pipe(
        switchMap(() => this.performAddNewProductToRequest$()),
        tap(() => this.requestService.reloadRequest()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  showNewProductFormToAdd() {
    this.isAddNewRequestProductFormShown = true;
  }

  performAddNewProductToRequest$() {
    if (this.formGroup.valid) {
      return this.dialogService$
        .open<boolean>(TUI_CONFIRM, {
          label: 'Вы действительно хотите добавить товар?',
          size: 's',
          closable: false,
          data: {
            yes: 'Да, добавить',
            no: 'Отмена',
          },
        })
        .pipe(
          filter(Boolean),
          switchMap(() =>
            this.requestApiService.addProductToRequest$({
              requestId: this.$request().id,
              requestProductId: this.newProductField.value!.id,
              amount: this.newProductAmountField.value!,
            }),
          ),
          tap(() => {
            this.isAddNewRequestProductFormShown = false;
            this.resetNewRequestProductForm();
          }),
        );
    }

    return EMPTY;
  }

  cancelAddingNewRequestProduct() {
    this.isAddNewRequestProductFormShown = false;
    this.resetNewRequestProductForm();
  }

  private resetNewRequestProductForm() {
    this.formGroup.reset({
      [FormFields.NewProduct]: null,
      [FormFields.NewProductAmount]: 1,
    });
  }
}
